import { useCallback, useEffect, useRef, useState } from 'react';
import { ALLOW_DOCUMENT_UNLOAD_EVENT } from '@/tiktok-platform/hooks/allowDocumentUnload';

function safeJsonSnapshot(value) {
  try {
    return JSON.stringify(value ?? null);
  } catch {
    return null;
  }
}

/**
 * 表单未保存离开守卫：刷新 / 关闭标签页 / 关闭浏览器时弹出确认。
 * （现代浏览器会显示系统默认文案，无法自定义。）
 *
 * @param {object} options
 * @param {unknown} options.value 参与对比的表单快照源（通常为 form）
 * @param {boolean} [options.enabled=true]
 * @param {boolean} [options.loading=false] 加载中不启用；结束后进入静默窗口再记 baseline
 * @param {number} [options.settleMs=1500] 加载完成后忽略自动回填的时长
 */
export function useUnsavedLeaveGuard({
  value,
  enabled = true,
  loading = false,
  settleMs = 1500,
} = {}) {
  const [isDirty, setIsDirty] = useState(false);
  const valueRef = useRef(value);
  const baselineRef = useRef(null);
  const allowLeaveRef = useRef(false);
  const stayOnPageCountRef = useRef(0);
  const silentUntilRef = useRef(0);

  valueRef.current = value;

  const clearDirty = useCallback(() => {
    baselineRef.current = safeJsonSnapshot(valueRef.current);
    allowLeaveRef.current = false;
    setIsDirty(false);
  }, []);

  /** 允许下一次离开（提交成功 / 已保存草稿即将跳转前调用） */
  const allowLeave = useCallback(() => {
    allowLeaveRef.current = true;
    setIsDirty(false);
  }, []);

  /**
   * 在页内异步操作期间抑制 beforeunload（如另存模板），避免误弹「是否离开网站」。
   * 操作结束后若仍停留本页，未保存状态按当前表单重新计算。
   */
  const runWithoutUnloadPrompt = useCallback(async (fn) => {
    stayOnPageCountRef.current += 1;
    try {
      return await fn();
    } finally {
      stayOnPageCountRef.current = Math.max(0, stayOnPageCountRef.current - 1);
      if (
        enabled
        && !loading
        && !allowLeaveRef.current
        && baselineRef.current != null
        && Date.now() >= silentUntilRef.current
      ) {
        const next = safeJsonSnapshot(valueRef.current);
        setIsDirty(next != null && next !== baselineRef.current);
      }
    }
  }, [enabled, loading]);

  // 加载结束后静默一段时间，再锁定 baseline（避开币种/仓库等自动回填）
  useEffect(() => {
    if (!enabled) {
      baselineRef.current = null;
      setIsDirty(false);
      return undefined;
    }
    if (loading) {
      baselineRef.current = null;
      allowLeaveRef.current = false;
      setIsDirty(false);
      return undefined;
    }
    const wait = Math.max(0, Number(settleMs) || 0);
    silentUntilRef.current = Date.now() + wait;
    const timer = window.setTimeout(() => {
      baselineRef.current = safeJsonSnapshot(valueRef.current);
      allowLeaveRef.current = false;
      setIsDirty(false);
    }, wait);
    return () => window.clearTimeout(timer);
  }, [enabled, loading, settleMs]);

  useEffect(() => {
    if (!enabled || loading || allowLeaveRef.current) return;
    if (baselineRef.current == null) return;
    if (Date.now() < silentUntilRef.current) return;
    const next = safeJsonSnapshot(value);
    if (next == null) return;
    setIsDirty(next !== baselineRef.current);
  }, [enabled, loading, value]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onAllowUnload = () => {
      allowLeaveRef.current = true;
      setIsDirty(false);
    };
    window.addEventListener(ALLOW_DOCUMENT_UNLOAD_EVENT, onAllowUnload);
    return () => window.removeEventListener(ALLOW_DOCUMENT_UNLOAD_EVENT, onAllowUnload);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onBeforeUnload = (event) => {
      if (allowLeaveRef.current || stayOnPageCountRef.current > 0 || !isDirty) return;
      event.preventDefault();
      // Chrome 等忽略自定义文案，但仍需赋值以触发系统提示
      event.returnValue = '当前修改的内容不会保存，确定要离开吗？';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [enabled, isDirty]);

  return { isDirty, allowLeave, clearDirty, runWithoutUnloadPrompt };
}

export default useUnsavedLeaveGuard;
